// Copyright 2023, mochabug AB
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import { fromJsonString, toJsonString } from '@bufbuild/protobuf';
import chalk from 'chalk';
import { Command, program } from 'commander';
import fg from 'fast-glob';
import figlet from 'figlet';
import fs from 'fs';
import path from 'path';
import semver from 'semver';
import updateNotifier from 'update-notifier';
import { add } from './add';
import { ManifestSchema } from './genproto/mochabugapis/adapt/plugins/v1/manifest_pb';
import { init } from './init';
import { waitForConsent } from './oauth2';
import { emulate, publish } from './publish';
import { readManifest } from './utils';

async function isValidUrl(urlString: string) {
  try {
    new URL(urlString);
    return true;
  } catch (error) {
    return false;
  }
}

interface ReadFilesResult {
  configuratorPath?: string;
  executorPath: string;
}

export async function readConfiguratorAndExecutor(
  dist: string
): Promise<ReadFilesResult> {
  // Build glob patterns for the two file types
  const configuratorPattern = path.join(dist, 'configurators.{js,cjs}');
  const executorPattern = path.join(dist, 'executors.{js,cjs}');

  // Use fast-glob to find matching files
  const [configuratorFiles, executorFiles] = await Promise.all([
    fg([configuratorPattern]),
    fg([executorPattern])
  ]);

  if (executorFiles.length === 0) {
    throw new Error(`No executor file found in ${dist}`);
  }

  // Choose the first matching file if there are multiple
  let configuratorPath: string | undefined;
  if (configuratorFiles.length > 0) {
    configuratorPath = configuratorFiles[0];
  }
  const executorPath = executorFiles[0];

  return {
    configuratorPath,
    executorPath
  };
}

function banner(): string {
  return chalk.yellowBright(figlet.textSync('Adaptkit'));
}

async function handleInit(dir: string, cmd: Command) {
  // Check for unexpected arguments
  if (cmd.args.length >= 1) {
    console.error(
      chalk.red(`Error: Invalid sub-command or arguments provided.`)
    );
    console.log(
      `For more information, use: ${chalk.blue('adaptkit help init')}`
    );
    process.exit(1);
  }

  // Continue with the usual logic if no unexpected arguments are found
  await init(dir);
}

async function handleAdd(src: string, cmd: Command) {
  if (cmd.args.length >= 1) {
    console.error(
      chalk.red(`Error: Invalid sub-command or arguments provided.`)
    );
    console.log(
      `For more information, use: ${chalk.blue('adaptkit help add')}`
    );
    process.exit(1);
  }

  const manifest = await readManifest('manifest.json', src);
  if (!manifest) {
    console.log(
      `For more information, use: ${chalk.blue('adaptkit help add')}`
    );
    process.exit(1);
  }
  await add(manifest);
}

async function handlePublish(
  endpoint: string,
  authUrl: string,
  client: string,
  insecure: boolean,
  errorUrl: string,
  src: string,
  assets: string,
  cdn: string,
  dist: string,
  cmd: Command
) {
  if (!(await isValidUrl(authUrl))) {
    console.error(
      chalk.red(
        `Error: The provided authority url is not a valid URL: ${authUrl}`
      )
    );
    console.log(
      `For more information, use: ${chalk.blue('adaptkit help publish')}`
    );
    process.exit(1);
  }
  if (!(await isValidUrl(endpoint))) {
    console.error(
      chalk.red(`Error: The provided endpoint is not valid: ${endpoint}`)
    );
    console.log(
      `For more information, use: ${chalk.blue('adaptkit help publish')}`
    );
    process.exit(1);
  }

  if (cmd.args.length >= 1) {
    console.error(
      chalk.red(`Error: Invalid sub-command or arguments provided.`)
    );
    console.log(
      `For more information, use: ${chalk.blue('adaptkit help publish')}`
    );
    process.exit(1);
  }

  const manifest = await readManifest('manifest.json', src);
  if (!manifest) {
    console.log(
      `For more information, use: ${chalk.blue('adaptkit help publish')}`
    );
    process.exit(1);
  }

  const res = await readConfiguratorAndExecutor(dist);

  if (insecure) {
    process.env.NODE_TLS_REJECT_UNAUTHORIZED = '0';
  }

  const accessToken = await waitForConsent(authUrl, errorUrl, client);
  if (!accessToken) {
    console.log('Could not fetch the acccess token. Terminating...');
    process.exit(1);
  }
  await publish({
    manifest,
    endpoint,
    accessToken,
    insecure,
    ...res,
    cdnFolder: cdn,
    assetsFolder: assets
  });
}

async function handleEmulate(
  endpoint: string,
  src: string,
  assets: string,
  cdn: string,
  dist: string,
  cmd: Command
) {
  if (!(await isValidUrl(endpoint))) {
    console.error(
      chalk.red(`Error: The provided endpoint is not valid: ${endpoint}`)
    );
    console.log(
      `For more information, use: ${chalk.blue('adaptkit help emulate')}`
    );
    process.exit(1);
  }
  if (cmd.args.length >= 1) {
    console.error(
      chalk.red(`Error: Invalid sub-command or arguments provided.`)
    );
    console.log(
      `For more information, use: ${chalk.blue('adaptkit help emulate')}`
    );
    process.exit(1);
  }

  const manifest = await readManifest('manifest.json', src);
  if (!manifest) {
    console.log(
      `For more information, use: ${chalk.blue('adaptkit help emulate')}`
    );
    process.exit(1);
  }

  const res = await readConfiguratorAndExecutor(dist);

  await emulate({
    manifest,
    endpoint,
    ...res,
    cdnFolder: cdn,
    assetsFolder: assets
  });
}

async function handleVersion(
  bump: 'patch' | 'minor' | 'major' | null,
  cmd: Command
) {
  if (cmd.args.length >= 1) {
    console.error(
      chalk.red(`Error: Invalid sub-command or arguments provided.`)
    );
    console.log(
      `For more information, use: ${chalk.blue('adaptkit help version')}`
    );
    process.exit(1);
  }

  if (!fs.existsSync('manifest.json')) {
    throw new Error(`Invalid command: Manifest file not found.`);
  }

  const manifest = fromJsonString(
    ManifestSchema,
    fs.readFileSync('manifest.json', 'utf-8')
  );
  if (!manifest) {
    console.log(
      `For more information, use: ${chalk.blue('adaptkit help version')}`
    );
    process.exit(1);
  }

  const manVer = semver.parse(manifest.version);
  if (!manVer) {
    console.error(
      chalk.red(
        `Error: The version in the manifest is not a valid semver version: ${manifest.version}`
      )
    );
    console.log(
      `For more information, use: ${chalk.blue('adaptkit help version')}`
    );
    process.exit(1);
  }

  if (bump === null) {
    console.error(
      chalk.red(
        'You must provide either --patch --minor or --major to bump the version.'
      )
    );
    console.log(
      `For more information, use: ${chalk.blue('adaptkit help version')}`
    );
    process.exit(1);
  }

  const prevVersion = manifest.version;
  manifest.version = manVer.inc(bump).toString();
  manifest.vertices = [];
  fs.writeFileSync(
    'manifest.json',
    toJsonString(ManifestSchema, manifest, {
      enumAsInteger: false,
      prettySpaces: 2
    })
  );
  console.log(
    chalk.green(`Bumped the version from ${prevVersion} to ${manifest.version}`)
  );
}

async function main() {
  const notifier = updateNotifier({
    pkg: __PACKAGE_JSON__
  });
  notifier.notify({ isGlobal: true, defer: false });

  program
    .name('Adaptkit')
    .version('1.0.0')
    .description(
      'Unleash the power of seamless plugin development with Adaptkit, your premier CLI companion for Mochabug Adapt. Effortlessly create, manage and deploy plugins with finesse. Explore the full potential of Adaptkit at ' +
        chalk.underline.blue('https://www.mochabug.com')
    )
    .configureHelp({
      sortSubcommands: true,
      sortOptions: true,
      showGlobalOptions: true,
      helpWidth: 140
    });

  const DEFAULT_INIT_DIRECTORY = './';
  program
    .command('init')
    .option(
      '-d, --dir <dir>',
      `Choose the directory to initiate your plugin's journey. Default: '${DEFAULT_INIT_DIRECTORY}'`,
      DEFAULT_INIT_DIRECTORY
    )
    .description(
      'Kickstart your plugin adventure. This command scaffolds a ready-to-go project structure.'
    )
    .action(({ dir }, cmd) => handleInit(dir, cmd));

  program
    .command('add')
    .option(
      '--src <folder>',
      'The folder where to recursively search for vertex.json files (default: src)',
      'src'
    )
    .description("Expand your plugin's capabilities")
    .action(({ src }, cmd) => handleAdd(src, cmd));

  program
    .command('publish')
    .option(
      '-e, --endpoint <url>',
      'URL of the gRPC server (including protocol)',
      'https://adapt-grpc-dev.mochabugapis.com'
    )
    .option(
      '--cdn <folder>',
      'Folder to upload the public plugin CDN resources (default: cdn)',
      'cdn'
    )
    .option(
      '--assets <folder>',
      'Folder to upload the private plugin assets to (default: assets)',
      'assets'
    )
    .option(
      '--src <folder>',
      'The folder where to recursively search for vertex.json files (default: src)',
      'src'
    )
    .option(
      '--dist <folder>',
      'The dist folder where the actual plugin code is. Searching for: configurator.[js|cjs], executor.[js|cjs]',
      'dist'
    )
    .option(
      '-a, --authority <authority>',
      chalk.yellow('Warning: Primarily for development purposes'),
      'https://auth-dev.mochabugapis.com'
    )
    .option(
      '-c, --client <client>',
      chalk.yellow('Warning: Primarily for development purposes'),
      '2d711bbb-4b57-4ad5-8bf0-f2269de5b71d'
    )
    .option(
      '--error-url <errorUrl>',
      chalk.yellow('Warning: Primarily for development purposes'),
      'https://accounts-dev.mochabug.com/services/error'
    )
    .option('--insecure', chalk.red('Ignore SSL certificate errors (insecure)'))
    .description(
      'Ready for liftoff? Publish your plugin to your organization with this command.'
    )
    .action(
      (
        {
          endpoint,
          authority,
          client,
          insecure,
          errorUrl,
          src,
          assets,
          cdn,
          dist
        },
        cmd
      ) =>
        handlePublish(
          endpoint,
          authority,
          client,
          insecure,
          errorUrl,
          src,
          assets,
          cdn,
          dist,
          cmd
        )
    );

  program
    .command('emulate')
    .option(
      '-e, --endpoint <url>',
      'URL of the emulator server (including protocol)',
      'http://localhost:51001'
    )
    .option(
      '--cdn <folder>',
      'Folder to upload the public plugin CDN resources (default: cdn)',
      'cdn'
    )
    .option(
      '--assets <folder>',
      'Folder to upload the private plugin assets to (default: assets)',
      'assets'
    )
    .option(
      '--src <folder>',
      'The folder where to recursively search for vertex.json files (default: src)',
      'src'
    )
    .option(
      '--dist <folder>',
      'The dist folder where the actual plugin code is. Searching for: configurator.[js|cjs], executor.[js|cjs]',
      'dist'
    )
    .description(
      'Take your plugin for a test drive. Best used in conjunction with your build pipeline.'
    )
    .action(({ endpoint, src, assets, cdn, dist }, cmd) =>
      handleEmulate(endpoint, src, assets, cdn, dist, cmd)
    );

  program
    .command('version')
    .option('--patch', 'Bump the manifest version to the next patch version')
    .option('--minor', 'Bump the manifest version to the next minor version')
    .option('--major', 'Bump the manifest version to the next major version')
    .description(
      'The version command can manipulate the version of the plugin manifest.'
    )
    .action(({ major, minor, patch }, cmd) =>
      handleVersion(
        major ? 'major' : minor ? 'minor' : patch ? 'patch' : null,
        cmd
      )
    );

  // Set a custom help action
  program.addHelpText('beforeAll', banner() + '\n');
  program.showHelpAfterError();

  try {
    await program.parseAsync(process.argv);
  } catch (error: any) {
    console.error(error);
    console.error('An error occurred:', error.message);
  }
}

main();
